import { PortalNotificationEventFlag } from '@app-core/models/core.model';
import { InputKey } from '@app-notification-setting/models/notification-configuration.model';

/**
 * @description Static configuration used for creating form
 * The keys in values denote notification_configuration key,
 * for which we need to create input field
 */
export const INPUT_KEYS: InputKey = {
  //health events
  ActiveUnprovisionedDevice: {},
  BadMountingDriverCamera: { key: 'badMountingDurationInMinutes', max: 60, min: 15 },
  BadMountingRoadCamera: { key: 'badMountingDurationInMinutes', max: 60, min: 15 },
  DataUsageReport: { key: 'dataUsageLimitPerDayInMB', max: 500, min: 50, recommended: 100 },
  DeviceOffline: { key: 'inactiveDays', max: 30, min: 1, recommended: 3 },
  DriverCameraLensCovered: { key: 'obstructedDurationInMinutes', max: 60, min: 15 },
  RoadCameraLensCovered: { key: 'obstructedDurationInMinutes', max: 60, min: 15 },
  SDCardIssues: {},
  SDCardUnavailable: {},
  TripTemporarilyDisabled: {},

  //safety events - harsh events
  Cornering: {
    enabledKey: 'corneringEnabled',
    allowedControls: ['speed'],
    speed: { configKey: 'corneringThreshold', defaultValue: 0, min: 0, max: 28, title: 'acceleration' },
  },
  'Harsh-Acceleration': {
    enabledKey: 'harshAccelerationEnabled',
    allowedControls: ['speed'],
    speed: { configKey: 'harshAccelerationThreshold', defaultValue: 0, min: 0, max: 28, title: 'acceleration' },
  },
  'Harsh-Braking': {
    enabledKey: 'hardBrakingEnabled',
    allowedControls: ['speed'],
    speed: { configKey: 'hardBrakingThreshold', defaultValue: 0, min: 0, max: 28, title: 'acceleration' },
  },

  // safety events - AI events
  // please update BE team if any keys are changed here
  DistractedDrivingGroupKey: {},
  'Cellphone-Distracted-Driving': {
    enabledKey: 'enableCellphoneDistraction',
    allowedControls: ['speed', 'duration'],
    speed: { configKey: 'triggerMinimumSpeedCellphoneDistractionMph', defaultValue: 0, min: 0, max: 20 },
    duration: { configKey: 'minDurationThresholdForCellphone', defaultValue: 3, min: 3, max: 10 },
  },
  'Smoking-Distracted-Driving': {
    enabledKey: 'enableSmokingDistraction',
    allowedControls: ['speed', 'duration'],
    speed: { configKey: 'triggerMinimumSpeedSmokingDistractionMph', defaultValue: 0, min: 0, max: 100 },
    duration: { configKey: 'minSmokingDurationInSeconds', defaultValue: 25, min: 25, max: 150 },
  },
  'Drinking-Distracted-Driving': {
    enabledKey: 'enableDrinkingDistraction',
    allowedControls: ['speed', 'duration'],
    speed: { configKey: 'triggerMinimumSpeedDrinkingDistractionMph', defaultValue: 0, min: 0, max: 100 },
    duration: { configKey: 'minDurationThresholdForDrinking', defaultValue: 3, min: 3, max: 10 },
  },
  'Texting-Distracted-Driving': {
    enabledKey: 'enableTextingDistraction',
    allowedControls: ['speed', 'duration'],
    speed: { configKey: 'triggerMinimumSpeedTextingDistractionMph', defaultValue: 0, min: 0, max: 100 },
    duration: { configKey: 'minDurationThresholdForTexting', defaultValue: 3, min: 3, max: 10 },
  },
  'Distracted-Driving': {
    enabledKey: 'distractedDrivingEnabled',
    allowedControls: ['speed', 'duration'],
    speed: { configKey: 'yawDistractionSpeedThresholdMph', defaultValue: 0, min: 0, max: 100 },
    duration: {
      configKey: ['minYawDistractionDurationSeconds', 'minimumPitchDistractionDurationSeconds'],
      defaultValue: 2,
      min: 2,
      max: 15,
    },
  },
  'Lizard-Eye-Distracted-Driving': {
    enabledKey: 'lizardEyeDistractionEnabled',
    allowedControls: ['speed', 'duration'],
    speed: { configKey: 'triggerMinimumSpeedLizardEyeDistractionMph', defaultValue: 0, min: 0, max: 20 },
    duration: { configKey: 'minEyeCloseDurationForLizardEye', defaultValue: 3, min: 3, max: 20 },
  },
  'Drowsy-Driving-Detected': {
    enabledKey: 'drowsyDrivingEnabled',
    allowedControls: ['speed', 'duration'],
    speed: { configKey: 'triggerMinimumSpeedDrowsinessMph', defaultValue: 0, min: 0, max: 20 },
    duration: { configKey: 'minEyeCloseDurationForDrowsiness', defaultValue: 1, min: 1, max: 10 },
  },
  MaxSpeedExceeded: {
    enabledKey: 'maxSpeedEnabled',
    allowedControls: ['speed'],
    speed: { configKey: 'speedingAllowance', defaultValue: 1, min: 1, max: 40 },
  },
  'Unbuckled-Seat-Belt': { enabledKey: 'enableUnbuckledSeatBelt' },
  'Tail-Gating-Detected': {
    enabledKey: 'tailgatingEnabled',
    allowedControls: ['severity'],
    severity: { configKey: 'tailgatingTTCThreshold', defaultValue: 0.25, min: 0.25, max: 3 },
  },
  'Forward-Collision-Warning': {
    enabledKey: 'forwardCollisionWarningEnabled',
    allowedControls: ['severity'],
    severity: { configKey: 'forwardCollisionWarningTTCThreshold', defaultValue: 0.25, min: 0.25, max: 3 },
  },
  'Traffic-STOP-Sign-Violated': {
    enabledKey: 'stopSignEnabled',
    allowedControls: ['speed'],
    speed: { configKey: 'stopSignMaximumSpeed', defaultValue: 1, min: 1, max: 19 },
  },
  'Traffic-Light-Violated': { enabledKey: 'trafficLightEnabled' },
  'Lane-Drift-Found': {
    enabledKey: 'laneDriftEnabled',
    allowedControls: ['duration'],
    duration: { configKey: 'laneDepartureDetectionDurationInSec', defaultValue: 2, min: 2, max: 15 },
  },

  // OTHER EVENTS
  'Custom-Triggered': {},
  PotentialCrash: {},
  'Roll-Over-Detected': {},
  DVRUpload: {},
};

export const DIAGNOSTIC_EVENTS = [
  'DeviceOffline',
  'RoadCameraLensCovered',
  'DriverCameraLensCovered',
  'BadMountingRoadCamera',
  'BadMountingDriverCamera',
  'DataUsageReport',
  'TripTemporarilyDisabled',
  'SDCardIssues',
  'SDCardUnavailable',
  'ActiveUnprovisionedDevice',
];

export const HARSH_EVENTS: PortalNotificationEventFlag[] = ['Cornering', 'Harsh-Acceleration', 'Harsh-Braking'];

export const DISTRACTED_EVENTS: PortalNotificationEventFlag[] = [
  'Cellphone-Distracted-Driving',
  'Smoking-Distracted-Driving',
  'Drinking-Distracted-Driving',
  'Texting-Distracted-Driving',
  'Distracted-Driving',
  'Lizard-Eye-Distracted-Driving',
];

export const AI_EVENTS: PortalNotificationEventFlag[] = [
  ...DISTRACTED_EVENTS,
  'Drowsy-Driving-Detected',
  'MaxSpeedExceeded',
  'Unbuckled-Seat-Belt',
  'Tail-Gating-Detected',
  'Forward-Collision-Warning',
  'Traffic-STOP-Sign-Violated',
  'Traffic-Light-Violated',
  //'Lane-Drift-Found',
];

export const SAFETY_EVENTS: PortalNotificationEventFlag[] = [...HARSH_EVENTS, ...AI_EVENTS];

export const OTHER_EVENTS: PortalNotificationEventFlag[] = ['Custom-Triggered', 'PotentialCrash', 'Roll-Over-Detected', 'DVRUpload'];

// Default values incase api doesn't contain them
export const EVENTS_DEFAULT_VALUES = {
  ...SAFETY_EVENTS.reduce((acc, event) => {
    acc[event] = { active: false, tagIds: [], emailNotificationEnabled: false };
    return acc;
  }, {}),
  ...OTHER_EVENTS.reduce((acc, event) => {
    acc[event] = { active: false, tagIds: [], emailNotificationEnabled: false };
    return acc;
  }, {}),
  DVRUpload: { active: true, emailNotificationEnabled: true },
};

/**
 * @description This is used to order the response of notification configuration
 */
export const ORDER_NOTIFICATION_CONFIGURATION = [...SAFETY_EVENTS, ...DIAGNOSTIC_EVENTS, ...OTHER_EVENTS];

export const ALLOWED_DYNAMIC_FC = ['speed', 'duration', 'severity'];

export default ORDER_NOTIFICATION_CONFIGURATION;
